import React, { useState, useRef, useCallback } from 'react'
import { Customer, Project, Partner } from '../../types'
import {
  EntityType, ParsedRow, BulkUploadResult,
  parseFile,
  generateRoleTypesTemplate, generateResourcesTemplate, generateCustomersTemplate,
  generateProjectsTemplate, generateTasksTemplate,
  validateRoleTypeRows, validateResourceRows, validateCustomerRows,
  validateProjectRows, validateTaskRows,
  insertRoleTypeRows, insertResourceRows, insertCustomerRows,
  insertProjectRows, insertTaskRows,
  generateErrorReport,
} from '../../services/bulkUploadService'
import { Download, Check, X, CheckCircle, AlertTriangle } from '../icons/Icons'
import s from './BulkUploadModal.module.css'

// ── Upload icon (not in Icons.tsx) ────────────────────────────────────────────
const UploadCloud: React.FC<{ size?: number }> = ({ size = 24 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="16 16 12 12 8 16"/><line x1="12" y1="12" x2="12" y2="21"/>
    <path d="M20.39 18.39A5 5 0 0 0 18 9h-1.26A8 8 0 1 0 3 16.3"/>
  </svg>
)

type Step = 'upload' | 'preview' | 'importing' | 'done'

const STEP_LABELS: Record<Step, string> = {
  upload: 'Upload File',
  preview: 'Preview & Validate',
  importing: 'Importing',
  done: 'Done',
}

const ENTITY_LABELS: Record<EntityType, string> = {
  roleTypes: 'Role Types',
  resources: 'Resources',
  customers: 'Customers',
  projects: 'Projects',
  tasks: 'Tasks',
}

const TEMPLATE_DESCRIPTIONS: Record<EntityType, string> = {
  roleTypes: 'Download the template, fill in role types with their cost and billing rates, then upload.',
  resources: 'Download the template, add one resource per row with name, email, and availability.',
  customers: 'Download the template, add one customer per row with industry and region details.',
  projects: 'Download the template, add projects referencing existing customer names.',
  tasks: 'Download the template, add tasks with row_ref and parent_row_ref to build hierarchy.',
}

interface Props {
  entityType: EntityType
  orgId: string
  userId: string
  customers?: Customer[]
  projects?: Project[]
  partners?: Partner[]
  onClose: () => void
  onSuccess: () => void
}

// Headers to show in the preview table (subset of template columns)
const PREVIEW_HEADERS: Record<EntityType, string[]> = {
  roleTypes: ['title', 'cost_rate', 'billing_rate', 'currency', 'active'],
  resources: ['title', 'email', 'primary_role', 'availability', 'active'],
  customers: ['title', 'account_manager', 'industry', 'region'],
  projects: ['title', 'customer_name', 'start_date', 'end_date', 'status'],
  tasks: ['row_ref', 'project_title', 'title', 'status', 'priority', 'parent_row_ref'],
}

export function BulkUploadModal({ entityType, orgId, userId, customers = [], projects = [], partners = [], onClose, onSuccess }: Props) {
  const [step, setStep] = useState<Step>('upload')
  const [dragOver, setDragOver] = useState(false)
  const [file, setFile] = useState<File | null>(null)
  const [parsing, setParsing] = useState(false)
  const [parsedRows, setParsedRows] = useState<ParsedRow[]>([])
  const [rawRows, setRawRows] = useState<Record<string, string>[]>([])
  const [progress, setProgress] = useState(0)
  const [progressTotal, setProgressTotal] = useState(0)
  const [result, setResult] = useState<BulkUploadResult | null>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)

  const downloadTemplate = () => {
    const fns: Record<EntityType, () => void> = {
      roleTypes: generateRoleTypesTemplate,
      resources: generateResourcesTemplate,
      customers: generateCustomersTemplate,
      projects: generateProjectsTemplate,
      tasks: generateTasksTemplate,
    }
    fns[entityType]()
  }

  const processFile = useCallback(async (f: File) => {
    setFile(f)
    setParsing(true)
    try {
      const raw = await parseFile(f)
      setRawRows(raw)
      let rows: ParsedRow[] = []
      if (entityType === 'roleTypes') rows = await validateRoleTypeRows(raw, orgId)
      else if (entityType === 'resources') rows = await validateResourceRows(raw, orgId)
      else if (entityType === 'customers') rows = await validateCustomerRows(raw, orgId)
      else if (entityType === 'projects') rows = await validateProjectRows(raw, orgId, customers, partners)
      else if (entityType === 'tasks') rows = await validateTaskRows(raw, orgId, projects)
      setParsedRows(rows)
      setStep('preview')
    } catch (err: any) {
      alert(`Failed to parse file: ${err?.message ?? 'Unknown error'}`)
    } finally {
      setParsing(false)
    }
  }, [entityType, orgId, customers, partners, projects])

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault()
    setDragOver(false)
    const f = e.dataTransfer.files[0]
    if (f) processFile(f)
  }

  const handleFileInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0]
    if (f) processFile(f)
  }

  const handleImport = async () => {
    setStep('importing')
    setProgress(0)
    const valid = parsedRows.filter(r => r.status !== 'error')
    setProgressTotal(valid.length)

    const onProgress = (done: number, total: number) => {
      setProgress(Math.round((done / total) * 100))
    }

    try {
      let res: BulkUploadResult
      if (entityType === 'roleTypes') res = await insertRoleTypeRows(parsedRows, orgId, userId, onProgress)
      else if (entityType === 'resources') res = await insertResourceRows(parsedRows, orgId, userId, onProgress)
      else if (entityType === 'customers') res = await insertCustomerRows(parsedRows, orgId, userId, onProgress)
      else if (entityType === 'projects') res = await insertProjectRows(parsedRows, orgId, userId, onProgress)
      else res = await insertTaskRows(parsedRows, orgId, userId, onProgress)
      setResult(res)
      setStep('done')
      if (res.succeeded > 0) onSuccess()
    } catch (err: any) {
      alert(`Import failed: ${err?.message ?? 'Unknown error'}`)
      setStep('preview')
    }
  }

  const stepOrder: Step[] = ['upload', 'preview', 'importing', 'done']
  const currentStepIdx = stepOrder.indexOf(step)

  const validCount = parsedRows.filter(r => r.status === 'valid').length
  const warnCount = parsedRows.filter(r => r.status === 'warning').length
  const errorCount = parsedRows.filter(r => r.status === 'error').length
  const importableCount = validCount + warnCount

  const previewHeaders = PREVIEW_HEADERS[entityType]

  return (
    <div className={s.overlay}>
      <div className={s.modal} role="dialog" aria-modal="true">

        {/* Header */}
        <div className={s.header}>
          <div className={s.headerLeft}>
            <h2>Bulk Upload — {ENTITY_LABELS[entityType]}</h2>
            <p>{TEMPLATE_DESCRIPTIONS[entityType]}</p>
          </div>
          <button className={s.closeBtn} onClick={onClose} aria-label="Close"><X size={18} /></button>
        </div>

        {/* Steps */}
        <div className={s.steps}>
          {(['upload', 'preview', 'importing', 'done'] as Step[]).map((st, idx) => {
            const isDone = currentStepIdx > idx
            const isActive = step === st
            return (
              <React.Fragment key={st}>
                <div className={s.step}>
                  <div className={`${s.stepNum} ${isActive ? s.active : isDone ? s.done : ''}`}>
                    {isDone ? <Check size={13} /> : idx + 1}
                  </div>
                  <span className={`${s.stepLabel} ${isActive ? s.active : isDone ? s.done : ''}`}>
                    {STEP_LABELS[st]}
                  </span>
                </div>
              </React.Fragment>
            )
          })}
        </div>

        {/* Body */}
        <div className={s.body}>

          {/* Step 1: Upload */}
          {step === 'upload' && !parsing && (
            <>
              <div className={s.templateSection}>
                <h3>Step 1 — Download the Template</h3>
                <p>
                  Use the official template to ensure your data is formatted correctly.
                  The template includes a sample row and notes explaining valid values.
                  Delete the sample and notes rows before uploading.
                </p>
                <button className={s.templateBtn} onClick={downloadTemplate}>
                  <Download size={14} /> Download {ENTITY_LABELS[entityType]} Template
                </button>
              </div>

              <div
                className={`${s.dropZone} ${dragOver ? s.dragOver : ''}`}
                onDragOver={e => { e.preventDefault(); setDragOver(true) }}
                onDragLeave={() => setDragOver(false)}
                onDrop={handleDrop}
                onClick={() => fileInputRef.current?.click()}
              >
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".xlsx,.xls"
                  onChange={handleFileInput}
                  onClick={e => e.stopPropagation()}
                />
                <div className={s.dropZoneIcon}><UploadCloud size={22} /></div>
                <div className={s.dropZoneText}>Drag and drop your Excel file here</div>
                <div className={s.dropZoneHint}>or click to browse — accepts .xlsx and .xls</div>
                {file && (
                  <div className={s.fileChosen}>
                    <Check size={13} /> {file.name}
                  </div>
                )}
              </div>
            </>
          )}

          {/* Parsing spinner */}
          {parsing && (
            <div className={s.parsingSpinner}>
              <div className={s.spinner} />
              Parsing and validating your file...
            </div>
          )}

          {/* Step 2: Preview */}
          {step === 'preview' && (
            <>
              <div className={s.summaryBar}>
                <span style={{ fontSize: 13, fontWeight: 700, color: '#374151', marginRight: 4 }}>
                  {parsedRows.length} row{parsedRows.length !== 1 ? 's' : ''} found
                </span>
                <span className={s.summaryItem}><span className={`${s.dot} ${s.valid}`} />{validCount} valid</span>
                <span className={s.summaryItem}><span className={`${s.dot} ${s.warning}`} />{warnCount} warning{warnCount !== 1 ? 's' : ''}</span>
                <span className={s.summaryItem}><span className={`${s.dot} ${s.error}`} />{errorCount} error{errorCount !== 1 ? 's' : ''}</span>
                {errorCount > 0 && (
                  <span style={{ fontSize: 12, color: '#dc2626', marginLeft: 'auto' }}>
                    {errorCount} row{errorCount !== 1 ? 's' : ''} will be skipped
                  </span>
                )}
              </div>

              <div className={s.previewTableWrap}>
                <table className={s.previewTable}>
                  <thead>
                    <tr>
                      <th>Status</th>
                      {previewHeaders.map(h => <th key={h}>{h}</th>)}
                      <th>Issues</th>
                    </tr>
                  </thead>
                  <tbody>
                    {parsedRows.map(row => (
                      <tr key={row.rowIndex} className={s[`row${row.status.charAt(0).toUpperCase() + row.status.slice(1)}` as keyof typeof s] as string}>
                        <td className={s.rowStatusCell}>
                          <span className={`${s.rowBadge} ${s[row.status]}`}>
                            {row.status === 'valid' && <Check size={10} />}
                            {row.status === 'warning' && <AlertTriangle size={10} />}
                            {row.status === 'error' && <X size={10} />}
                            {row.status}
                          </span>
                        </td>
                        {previewHeaders.map(h => (
                          <td key={h} title={row.raw[h] ?? ''}>{row.raw[h] ?? ''}</td>
                        ))}
                        <td>
                          <div className={s.rowMessages}>
                            {row.errors.map((e, i) => <div key={i} className={s.err}>{e}</div>)}
                            {row.warnings.map((w, i) => <div key={i} className={s.warn}>{w}</div>)}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}

          {/* Step 3: Importing */}
          {step === 'importing' && (
            <div style={{ padding: '16px 0' }}>
              <p style={{ fontSize: 14, color: '#374151', marginBottom: 16 }}>
                Importing {progressTotal} row{progressTotal !== 1 ? 's' : ''}... Please wait.
              </p>
              <div className={s.progressWrap}>
                <div className={s.progressBar}>
                  <div className={s.progressFill} style={{ width: `${progress}%` }} />
                </div>
                <div className={s.progressLabel}>{progress}% complete</div>
              </div>
            </div>
          )}

          {/* Step 4: Done */}
          {step === 'done' && result && (
            <div className={`${s.resultsBox} ${
              result.failed === 0 ? s.success : result.succeeded === 0 ? s.allFailed : s.partial
            }`}>
              <h3>
                {result.failed === 0 ? 'Import Complete' : result.succeeded === 0 ? 'Import Failed' : 'Import Complete with Errors'}
              </h3>
              <p className={s.resultsStat} style={{ color: '#166534' }}>
                {result.succeeded} row{result.succeeded !== 1 ? 's' : ''} imported successfully
              </p>
              {result.failed > 0 && (
                <p className={s.resultsStat} style={{ color: '#dc2626' }}>
                  {result.failed} row{result.failed !== 1 ? 's' : ''} failed
                </p>
              )}
              {result.failedRows.length > 0 && (
                <div className={s.failList}>
                  {result.failedRows.map((f, i) => (
                    <div key={i} className={s.failItem}>
                      Row {f.rowIndex}: <strong>{f.title || 'Untitled'}</strong> — {f.error}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className={s.footer}>
          {step === 'upload' && (
            <button className={s.btnSecondary} onClick={onClose}>Cancel</button>
          )}

          {step === 'preview' && (
            <>
              <button className={s.btnSecondary} onClick={() => { setFile(null); setParsedRows([]); setRawRows([]); setStep('upload') }}>
                Back
              </button>
              <button
                className={s.btnPrimary}
                disabled={importableCount === 0}
                onClick={handleImport}
              >
                Import {importableCount} row{importableCount !== 1 ? 's' : ''}
              </button>
            </>
          )}

          {step === 'importing' && (
            <button className={s.btnSecondary} disabled>Importing...</button>
          )}

          {step === 'done' && result && (
            <>
              {result.failedRows.length > 0 && rawRows.length > 0 && (
                <button
                  className={s.btnSecondary}
                  onClick={() => generateErrorReport(rawRows, result.failedRows, entityType)}
                >
                  <Download size={13} /> Download Error Report
                </button>
              )}
              <button className={s.btnPrimary} onClick={onClose}>
                <CheckCircle size={14} /> Done
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  )
}
